import { Heading, HStack, Stack } from "@chakra-ui/layout";
import {
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Switch,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { LoadingOverlay } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { TaskItemListing } from "@src/components/ui-kit/TaskItem/TaskItemListing";
import { NotFoundProject } from "@src/components/widgets/ErrorPage";
import { headTitle } from "@src/utils/head-title";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import Head from "next/head";
import React, {
  type FunctionComponent,
  useEffect,
  useLayoutEffect,
} from "react";
import { Header } from "./Header";

interface DetailProps {
  id: string;
}

export const ProjectDetailForClient: FunctionComponent<DetailProps> = observer(
  function ProjectDetailForClient({ id }) {
    const {
      projectDetailForClientsStore: store,
      workspaceStore,
      taskDetailModalStore,
    } = useStore();

    useEffect(() => {
      store.fetchData(id);
    }, [id]);

    useLayoutEffect(() => {
      workspaceStore.settings?.show_done_tasks
        ? store.showDoneTasks.on()
        : store.showDoneTasks.off();
    }, [workspaceStore.settings?.show_done_tasks]);

    if (!store.isLoading && !store.project) {
      return <NotFoundProject />;
    }

    return (
      <React.Fragment>
        <Head>
          <title>{headTitle(store.project?.title)}</title>
        </Head>

        <LoadingOverlay isLoading={store.isLoading} />

        {store?.project && <Header />}

        <Card mb="3">
          <CardBody>
            <HStack justifyContent="space-between" alignItems="center">
              <Heading size="sm">
                <Trans>Tasks</Trans>
              </Heading>

              <HStack>
                <FormControl alignItems="center" display="flex">
                  <Switch
                    mr="2"
                    id="show-done-tasks"
                    isChecked={store.showDoneTasks.value}
                    onChange={store.showDoneTasks.toggle}
                  />
                  <FormLabel mb="0" htmlFor="show-done-tasks">
                    <Trans>Show done tasks</Trans>
                  </FormLabel>
                </FormControl>
                <Button
                  minW="fit-content"
                  colorScheme="violet"
                  leftIcon={<Icon name="plus" />}
                  onClick={() => store.handleAddTaskClick(id)}
                >
                  <Trans>Task</Trans>
                </Button>
              </HStack>
            </HStack>
          </CardBody>
        </Card>

        <Stack spacing="0">
          {store.tasksToShow.map((task) => (
            <TaskItemListing
              key={task.id}
              task={task}
              isSubtask
              onClick={(task) =>
                taskDetailModalStore.disclosureState.onOpen({
                  id: task.id,
                  onChange: () => store.fetchData(id),
                  onDelete: () => store.fetchData(id),
                  onDuplicate: () => store.fetchData(id),
                })
              }
              onCreateSubtask={() => {
                store.fetchData(id);
              }}
            />
          ))}
        </Stack>
      </React.Fragment>
    );
  },
);
